import { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle, XCircle, Info, Clock, RefreshCw, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

interface IntegrationLog {
  id: string;
  integration_name: string;
  action: string;
  status: 'success' | 'error' | 'info';
  message: string;
  request_summary: Record<string, unknown> | null;
  response_summary: Record<string, unknown> | null;
  duration_ms: number | null;
  user_email: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface ListEnvelope {
  rows: IntegrationLog[];
  total: number;
}

interface IntegrationLogViewerProps {
  isOpen: boolean;
  onClose: () => void;
  integrationName: string;
  integrationLabel: string;
}

const STATUS_CONFIG = {
  success: { icon: CheckCircle, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-500/10', border: 'border-green-200 dark:border-green-500/30', label: 'Success' },
  error: { icon: XCircle, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-500/10', border: 'border-red-200 dark:border-red-500/30', label: 'Error' },
  info: { icon: Info, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-500/10', border: 'border-blue-200 dark:border-blue-500/30', label: 'Info' },
};

const TIME_RANGES = [
  { value: '1h', label: '1 hour' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

function getTimeRangeDate(range: string): Date {
  const now = new Date();
  switch (range) {
    case '1h': return new Date(now.getTime() - 60 * 60 * 1000);
    case '24h': return new Date(now.getTime() - 24 * 60 * 60 * 1000);
    case '7d': return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case '30d': return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    default: return new Date(now.getTime() - 24 * 60 * 60 * 1000);
  }
}

function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function JsonBlock({ data }: { data: Record<string, unknown> | null }) {
  if (!data || Object.keys(data).length === 0) return null;
  return (
    <pre className="text-[11px] leading-relaxed font-mono text-theme-secondary bg-subtle rounded p-2 overflow-x-auto max-h-48 whitespace-pre-wrap break-all">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

export function IntegrationLogViewer({ isOpen, onClose, integrationName, integrationLabel }: IntegrationLogViewerProps) {
  const { t } = useTranslation('common');
  const [logs, setLogs] = useState<IntegrationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('24h');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const since = getTimeRangeDate(timeRange).toISOString();
      const params = new URLSearchParams();
      params.set('integration_name', integrationName);
      params.set('from', since);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('limit', '200');
      const result = await apiClient.get<ListEnvelope>(`/api/integration-logs?${params.toString()}`);
      setLogs(result.rows);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [integrationName, timeRange, statusFilter]);

  useEffect(() => {
    if (isOpen) {
      void fetchLogs();
    }
  }, [isOpen, fetchLogs]);

  useEffect(() => {
    if (!isOpen) {
      setExpandedId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const successCount = logs.filter((l) => l.status === 'success').length;
  const errorCount = logs.filter((l) => l.status === 'error').length;
  const infoCount = logs.filter((l) => l.status === 'info').length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-surface rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line">
          <div>
            <h3 className="text-lg font-semibold text-theme-primary">
              {integrationLabel} {t('integrations.logs', 'Logs')}
            </h3>
            <p className="text-xs text-theme-secondary mt-0.5">
              {t('integrations.logsSubtitle', 'Integration activity and debug logging')}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-subtle transition-colors">
            <X size={20} className="text-theme-secondary" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-line bg-subtle">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Clock size={14} className="text-theme-secondary" />
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="text-xs border border-line bg-surface text-theme-primary rounded px-2 py-1 focus:ring-1 focus:ring-blue-500"
              >
                {TIME_RANGES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs border border-line bg-surface text-theme-primary rounded px-2 py-1 focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">{t('integrations.allStatuses', 'All Statuses')}</option>
              <option value="success">{t('integrations.successOnly', 'Success')}</option>
              <option value="error">{t('integrations.errorsOnly', 'Errors')}</option>
              <option value="info">{t('integrations.infoOnly', 'Info')}</option>
            </select>

            <button
              onClick={fetchLogs}
              disabled={loading}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              {t('integrations.refresh', 'Refresh')}
            </button>

            <div className="flex items-center gap-3 ml-auto text-[11px] text-theme-secondary">
              <span className="flex items-center gap-1">
                <CheckCircle size={12} className="text-green-500" /> {successCount}
              </span>
              <span className="flex items-center gap-1">
                <XCircle size={12} className="text-red-500" /> {errorCount}
              </span>
              <span className="flex items-center gap-1">
                <Info size={12} className="text-blue-500" /> {infoCount}
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-theme-secondary" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-theme-secondary">
              <Info size={32} className="mb-2" />
              <p className="text-sm font-medium">{t('integrations.noLogs', 'No logs found')}</p>
              <p className="text-xs mt-1">{t('integrations.noLogsHint', 'Integration activity will appear here after usage')}</p>
            </div>
          ) : (
            <div className="divide-y divide-line">
              {logs.map((log) => {
                const config = STATUS_CONFIG[log.status] || STATUS_CONFIG.info;
                const StatusIcon = config.icon;
                const isExpanded = expandedId === log.id;

                return (
                  <div key={log.id} className="hover:bg-subtle transition-colors">
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : log.id)}
                      className="w-full text-left px-6 py-3 flex items-start gap-3"
                    >
                      <StatusIcon size={16} className={`${config.color} shrink-0 mt-0.5`} />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${config.bg} ${config.color} ${config.border} border`}>
                            {log.action}
                          </span>
                          <span className="text-xs text-theme-secondary truncate flex-1">{log.message}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-theme-secondary">
                          <span>{formatTimestamp(log.created_at)}</span>
                          {log.duration_ms !== null && (
                            <span>{formatDuration(log.duration_ms)}</span>
                          )}
                          {log.user_email && <span>{log.user_email}</span>}
                        </div>
                      </div>

                      {(log.request_summary || log.response_summary || log.metadata) && (
                        isExpanded
                          ? <ChevronDown size={14} className="text-theme-secondary shrink-0 mt-1" />
                          : <ChevronRight size={14} className="text-theme-secondary shrink-0 mt-1" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="px-6 pb-4 pl-[52px] space-y-3">
                        {log.request_summary && (
                          <div>
                            <p className="text-[10px] font-semibold text-theme-secondary uppercase tracking-wider mb-1">
                              {t('integrations.request', 'Request')}
                            </p>
                            <JsonBlock data={log.request_summary} />
                          </div>
                        )}
                        {log.response_summary && (
                          <div>
                            <p className="text-[10px] font-semibold text-theme-secondary uppercase tracking-wider mb-1">
                              {t('integrations.response', 'Response')}
                            </p>
                            <JsonBlock data={log.response_summary} />
                          </div>
                        )}
                        {log.metadata && (
                          <div>
                            <p className="text-[10px] font-semibold text-theme-secondary uppercase tracking-wider mb-1">
                              {t('integrations.metadata', 'Metadata')}
                            </p>
                            <JsonBlock data={log.metadata} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-line bg-subtle flex items-center justify-between">
          <span className="text-xs text-theme-secondary">
            {logs.length} {t('integrations.logEntries', 'entries')}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-theme-secondary bg-surface border border-line rounded-lg hover:bg-subtle transition-colors"
          >
            {t('actions.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
